<script setup>
import { CircleXIcon, ChevronsUpDownIcon, Loader2 } from 'lucide-vue-next'
import { computed, ref, watch } from 'vue'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { InputGroup, InputGroupAddon, InputGroupButton, InputGroupInput } from '@/components/ui/input-group'

import {Separator} from '@/components/ui/separator'

const props = defineProps({
  modelValue: {
    type: [String, Number, Array, null],
    default: null
  },
  options: {
    type: Array,
    default: () => []
  },
  placeholder: {
    type: String,
    default: 'Select option...'
  },
  emptyText: {
    type: String,
    default: 'No option found.'
  },
  searchPlaceholder: {
    type: String,
    default: 'Search...'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  formatLabel: {
    type: Function,
    default: null
  },
  triggerWidth: {
    type: String,
    default: 'w-sm'
  },
  width: {
    type: String,
    default: 'w-60'
  },
  disabled: {
    type: Boolean,
    default: false
  },
  teleportTarget: {
    type: [String, Object],
    default: '#shadcn-portal'
  },
  optionClass: {
    type: Array,
    default: () => [
      'text-left', 
      'my-2',
      'cursor-pointer',
      'hover:bg-accent',
      'hover:text-accent-foreground',
      'text-base'
    ]
  },
  alignOptionsContainer: {
    type: String,
    default: 'center'
  },
  clearable: {
    type: Boolean,
    default: false
  },
  multiple: {
    type: Boolean,
    default: false
  },
  errorMessages: {
    type: Array,
    default: () => []
  },
  loading: {
    type: Boolean,
    default: false
  },
  loadingText: {
    type: String,
    default: 'Loading...'
  }
})

const emit = defineEmits(['update:modelValue', 'change', 'select', 'deselect'])

const open = ref(false)
const setOpen = (value) => {
  open.value = value
}

const selectedValue = ref(props.multiple ? [] : null)
const setSelectedValue = (value) => {
  selectedValue.value = value
}

// Check if a value is selected (works for both single and multi mode)
const isSelected = (value) => {
  if (props.multiple) {
    return Array.isArray(selectedValue.value) && selectedValue.value.includes(value)
  }
  return selectedValue.value === value
}

// Toggle selection in multi-select mode or set in single-select mode
const toggleSelection = (value) => {
  if (props.multiple) {
    const currentValues = Array.isArray(selectedValue.value) ? [...selectedValue.value] : []
    const index = currentValues.indexOf(value)
    
    if (index > -1) {
      // Deselect
      currentValues.splice(index, 1)
      const option = props.options.find(opt => opt[props.valueKey] === value)
      if (option) {
        emit('deselect', option)
      }
    } else {
      // Select
      currentValues.push(value)
      const option = props.options.find(opt => opt[props.valueKey] === value)
      if (option) {
        emit('select', option)
      }
    }
    
    setSelectedValue(currentValues)
    emit('update:modelValue', currentValues)
    emit('change', currentValues)
  } else {
    // Single select mode - existing behavior
    setSelectedValue(value)
  }
}

const selectedOption = computed(() => {
  if (props.multiple) {
    return props.options.filter(option => 
      Array.isArray(selectedValue.value) && selectedValue.value.includes(option[props.valueKey])
    )
  }
  return props.options.find(option => option[props.valueKey] === selectedValue.value)
})

const selectedLabel = computed(() => {
  if (props.multiple) {
    const selected = selectedOption.value
    if (!selected || selected.length === 0) return props.placeholder
    
    if (selected.length === 1) {
      return props.formatLabel ? props.formatLabel(selected[0]) : selected[0][props.labelKey]
    }
    
    return `${selected.length} options selected`
  }
  
  if (!props.modelValue) return props.placeholder
  if (selectedOption.value) {
    return props.formatLabel ? props.formatLabel(selectedOption.value) : selectedOption.value[props.labelKey]
  }
  return props.placeholder
})

const optionClasses = computed(() => {
  return props.optionClass.join(' ')
})

const showClearButton = computed(() => {
  if (props.multiple) {
    return props.clearable && Array.isArray(selectedValue.value) && selectedValue.value.length > 0
  }
  return props.clearable && selectedLabel.value !== props.placeholder
})

const clearSelection = () => {
  if (props.multiple) {
    setSelectedValue([])
    emit('update:modelValue', [])
    emit('change', [])
  } else {
    setSelectedValue(null)
    emit('update:modelValue', null)
    emit('change', null)
  }
}

// Watch for external modelValue changes and sync with selectedValue
watch(() => props.modelValue, (newValue) => {
  if (props.multiple) {
    const currentValue = Array.isArray(selectedValue.value) ? selectedValue.value : []
    const newValueArray = Array.isArray(newValue) ? newValue : []
    if (JSON.stringify(currentValue) !== JSON.stringify(newValueArray)) {
      setSelectedValue(newValueArray)
    }
  } else {
    if (newValue !== selectedValue.value) {
      setSelectedValue(newValue)
    }
  }
}, { immediate: true })

// Watch for selectedValue changes and emit to parent (single-select mode only)
watch(selectedValue, (newValue) => {
  if (!props.multiple && newValue !== props.modelValue) {
    const option = props.options.find(opt => opt[props.valueKey] === newValue)
    emit('update:modelValue', newValue)
    emit('change', newValue)
    if (option) {
      emit('select', option)
    }
    setOpen(false)
  }
})

const hasErrors = computed(() => props.errorMessages && props.errorMessages.length > 0)

const isDisabled = computed(() => props.disabled || props.loading)
</script>

<template>
    <div class="w-full">
      <Popover v-model:open="open">
        <PopoverTrigger as-child>
          <InputGroup :class="[isDisabled ? 'bg-disabled-bg opacity-100' : 'bg-white', hasErrors ? 'border-red-500' : '']">
            <InputGroupInput 
              :model-value="loading ? loadingText : selectedLabel" 
              :disabled="isDisabled" 
              class="bg-white disabled:bg-transparent" 
              :class="{ 'text-text-soft': !selectedOption && !loading, 'text-text-disabled': isDisabled }" 
              readonly 
            />
            <InputGroupAddon v-if="showClearButton && !loading" align="inline-end">
              <Button
                variant="ghost"
                :aria-expanded="open"
                :disabled="isDisabled"
                @click.stop="clearSelection"
                size="icon-sm"
                class="bg-white hover:bg-transparent hover:text-gray-700"
              >
                <CircleXIcon class="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </InputGroupAddon>
            <InputGroupAddon align="inline-end">
              <Button
                variant="ghost"
                role="combobox"
                :aria-expanded="open"
                :class="cn(
                  'bg-transparent hover:bg-transparent hover:text-gray-700 disabled:bg-transparent'
                )"
                :disabled="isDisabled"
                size="icon-sm"
              >
                <Loader2 v-if="loading" class="h-4 w-4 animate-spin opacity-50" />
                <ChevronsUpDownIcon v-else class="h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </InputGroupAddon>
          </InputGroup>
        </PopoverTrigger>
        <PopoverContent 
          :teleport-to="teleportTarget" 
          :class="width"
          :align="alignOptionsContainer"
        >
          <Command v-model="selectedValue">
            <CommandInput :placeholder="searchPlaceholder" />
            <Separator class="my-2 bg-primary-border-color" />
            <CommandList>
              <CommandEmpty>{{ emptyText }}</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  v-for="option in options"
                  :key="option[valueKey]"
                  :value="option[valueKey]"
                  @select="toggleSelection(option[valueKey])"
                  :class="cn(
                    optionClasses,
                    isSelected(option[valueKey])
                      ? 'bg-gray-500 text-white data-[highlighted]:bg-gray-500 data-[highlighted]:text-white' 
                      : 'hover:bg-accent hover:text-accent-foreground'
                  )"
                >
                  {{ formatLabel ? formatLabel(option) : option[labelKey] }}
                </CommandItem>
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
      <div v-if="hasErrors" class="mt-1 space-y-1">
        <p
          v-for="(error, index) in errorMessages"
          :key="index"
          class="text-xs text-red-500"
        >
          {{ error }}
        </p>
      </div>
    </div>
</template>

<style>
[cmdk-list] {
  scroll-padding-block-start: 8px;
  scroll-padding-block-end: 8px;
}
</style>